import {
  QuoteSheetLineItemDTO,
  SupplierProductDTO,
} from '@/api/procurement/quote-sheets-types';
import { ProductV2DTO } from '@/types/application/inventory-management/ProductV2DTO';
import { ProductVariantDTO } from '@/types/application/inventory-management/ProductVariantDTO';

export function getProductName(lineItem: QuoteSheetLineItemDTO): string {
  // Priority: 1) Selected supplier product, 2) Suggested supplier product, 3) Selected product variant, 4) Suggested product variant, 5) Selected product, 6) Suggested product, 7) Raw data
  const effectiveSupplierProduct = getEffectiveSupplierProduct(lineItem);
  if (effectiveSupplierProduct?.name) {
    return effectiveSupplierProduct.name;
  }

  const effectiveProductVariant = getEffectiveProductVariant(lineItem);
  if (effectiveProductVariant?.originMetadata?.NAMEINVC) {
    return effectiveProductVariant.originMetadata.NAMEINVC as string;
  }

  const effectiveProduct = getEffectiveProduct(lineItem);
  if (effectiveProduct?.name) {
    return effectiveProduct.name;
  }

  if (lineItem.rawData?.productName) {
    return String(lineItem.rawData.productName);
  }
  return '';
}

/**
 * Returns a product-centric display name that prefers the Product name and does not
 * fall back to Product Variant naming. This is useful when labeling rows that
 * represent a Product grouping, to avoid showing a variant name in place of the product.
 */
export function getProductNamePreferProduct(
  lineItem: QuoteSheetLineItemDTO,
): string {
  const effectiveProduct = getEffectiveProduct(lineItem);
  if (effectiveProduct?.name) {
    return effectiveProduct.name;
  }

  // If there is no Product available, fall back to the supplier product name,
  // then raw data. Intentionally do NOT use the variant name here.
  const effectiveSupplierProduct = getEffectiveSupplierProduct(lineItem);
  if (effectiveSupplierProduct?.name) {
    return effectiveSupplierProduct.name;
  }

  if (lineItem.rawData?.productName) {
    return String(lineItem.rawData.productName);
  }

  return '';
}

export function getEffectiveSupplierProduct(
  lineItem: QuoteSheetLineItemDTO,
): SupplierProductDTO | null {
  // Priority: selected over suggested
  return (
    lineItem.selectedSupplierProduct ||
    lineItem.suggestedSupplierProduct ||
    null
  );
}

export function getEffectiveProduct(
  lineItem: QuoteSheetLineItemDTO,
): ProductV2DTO | null {
  // Priority: selected over suggested
  return lineItem.selectedProduct || lineItem.suggestedProduct || null;
}

export function getEffectiveProductVariant(
  lineItem: QuoteSheetLineItemDTO,
): ProductVariantDTO | null {
  // For product variants, we check the supplier product first, then fallback to direct variant references
  const effectiveSupplierProduct = getEffectiveSupplierProduct(lineItem);
  if (effectiveSupplierProduct?.productVariant) {
    return effectiveSupplierProduct.productVariant;
  }

  // If no supplier product, check suggested product variant
  return lineItem.selectedProduct
    ? null
    : lineItem.suggestedProductVariant || null;
}

export function getEffectiveProductId(
  lineItem: QuoteSheetLineItemDTO,
): string | null {
  const supplierProduct = getEffectiveSupplierProduct(lineItem);
  if (supplierProduct?.productId) {
    return supplierProduct.productId;
  }

  return lineItem.selectedProductId || lineItem.suggestedProductId || null;
}

export function getEffectiveProductVariantId(
  lineItem: QuoteSheetLineItemDTO,
): string | null {
  const supplierProduct = getEffectiveSupplierProduct(lineItem);
  if (supplierProduct?.productVariantId) {
    return supplierProduct.productVariantId;
  }

  return lineItem.selectedProductId
    ? null
    : lineItem.suggestedProductVariantId || null;
}

export function hasAnySelection(lineItem: QuoteSheetLineItemDTO): boolean {
  return !!(
    lineItem.selectedSupplierProductId ||
    lineItem.selectedProductId ||
    lineItem.suggestedSupplierProductId ||
    lineItem.suggestedProductId ||
    lineItem.suggestedProductVariantId
  );
}
